import { DatePicker, Flex, Form, Input, Upload, message } from "antd";
import type { FormInstance } from "antd/lib/form";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useState } from "react";
import { useUploadFileMutation } from "../../../../app/features/auth/auth.api";
import PassportIcon from "../../../../assets/icons/passport.svg";
import { DATE_FORMATS, ENV_CONFIG } from "../../../../config/global.config";
import { toast } from "../../../../lib/toast";
import UploadIcon from "../_icons/UploadIcon";

dayjs.extend(customParseFormat);

type Props = {
  form: FormInstance<any>;
};

export default function PassportInfo({ form }: Props) {
  const [uploadPassportFile, { isLoading: isPassportUploading }] =
    useUploadFileMutation();
  const [passportUrl, setPassportUrl] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize passportUrl from form if exists
  useEffect(() => {
    const url = form.getFieldValue("passportFileUrl");
    if (url) {
      setPassportUrl(url);
    }
  }, [form]);

  const handleUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("শুধুমাত্র ছবি আপলোড করতে পারবেন!");
      return Upload.LIST_IGNORE;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await uploadPassportFile(formData).unwrap();
      if (response?.file?.url) {
        const fullUrl = `${ENV_CONFIG.IMAGE_URL}/${response.file.url}`;
        setPassportUrl(fullUrl);
        form.setFieldValue("passportFileUrl", fullUrl);

        onSuccess("ok");
        toast("success", "আপলোড সফল হয়েছে!");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        onError("Upload failed");
        toast("error", "আপলোড ব্যর্থ হয়েছে: কোনো URL পাওয়া যায়নি");
      }
    } catch (error) {
      console.error("Upload Error:", error);
      onError(error);
      toast("error", "আপলোড ব্যর্থ হয়েছে!");
    }
  };

  return (
    <>
      <Flex align="center" className="mb-3" gap={16}>
        <div>
          <img
            className="w-28 h-32 object-contain rounded border border-theme-zinc-200"
            src={passportUrl || PassportIcon}
          />
        </div>
        <div className="w-full flex-1 h-32">
          <Form.Item name="passportFileUrl" hidden>
            <Input />
          </Form.Item>
          <Upload.Dragger
            customRequest={handleUpload}
            showUploadList={false}
            accept=".png,.jpg,.jpeg"
            className="w-full h-full custom-upload !bg-white !rounded-md !border-theme-primary"
            disabled={isPassportUploading}
          >
            <Flex align="center" justify="center" vertical>
              {isPassportUploading ? (
                <span className="text-theme-primary animate-pulse mt-8">
                  আপলোড হচ্ছে...
                </span>
              ) : passportUrl ? (
                <div className="text-center mt-4">
                  {showSuccess && (
                    <div className="text-green-600 font-medium mb-1">
                      আপলোড সফল হয়েছে!
                    </div>
                  )}
                  <div className="text-xs text-gray-500 break-all px-4">
                    {passportUrl.split("/").pop()}
                  </div>
                  <div className="text-xs text-theme-primary mt-1">
                    পরিবর্তন করতে ক্লিক করুন
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-center mt-4">
                    <UploadIcon />
                  </div>
                  <div className="text-Base text-base font-medium text-theme-primary leading-7 mt-2.5">
                    পাসপোর্ট কপি আপলোড করুন
                  </div>
                </>
              )}
            </Flex>
          </Upload.Dragger>
        </div>
      </Flex>

      <Form.Item
        name="passportNumber"
        label="পাসপোর্ট নম্বর"
        validateFirst
        rules={[
          {
            required: true,
            message: "পাসপোর্ট নম্বর আবশ্যক",
          },
          {
            len: 9,
            message: "পাসপোর্ট নম্বর অবশ্যই ৯ অক্ষরের হতে হবে",
          },
          // {
          //   pattern: /^[A-Z]{2}\d{7}$/,
          //   message:
          //     "পাসপোর্ট নম্বর অবশ্যই ২টি বড় হাতের ইংরেজি অক্ষর এবং ৭টি সংখ্যা হতে হবে",
          // },
        ]}
      >
        <Input size="large" placeholder="AB1234567" maxLength={9} />
      </Form.Item>
      <Form.Item
        name="passportExpireAt"
        label="মেয়াদ শেষ"
        validateFirst
        rules={[
          {
            required: true,
            message: "মেয়াদ শেষ তারিখ দিন",
          },
          {
            validator: async (_: any, value: string) => {
              if (!value) {
                return Promise.resolve();
              }
              const parsed = dayjs(
                value,
                [
                  DATE_FORMATS.dbDate,
                  DATE_FORMATS.displayDate,
                  "DD/MM/YYYY",
                  "DD-MM-YYYY",
                ],
                true
              );
              if (!parsed.isValid()) {
                return Promise.reject(
                  new Error(
                    "Please enter a valid date in the format dd/mm/yyyy."
                  )
                );
              }
              return Promise.resolve();
            },
          },
        ]}
        getValueFromEvent={(date: Dayjs | null, dateString: string | string[]) => {
          if (date && date.isValid()) {
            return date.format(DATE_FORMATS.dbDate);
          }
          const str = Array.isArray(dateString) ? dateString[0] : dateString;
          return str ? str : null;
        }}
        getValueProps={(value) => {
          if (!value) return { value: null };
          const parsed = dayjs(
            value,
            [
              DATE_FORMATS.dbDate,
              DATE_FORMATS.displayDate,
              "DD/MM/YYYY",
              "DD-MM-YYYY",
            ],
            true
          );
          return { value: parsed.isValid() ? parsed : null };
        }}
      >
        <DatePicker
          format={[
            DATE_FORMATS.displayDate,
            "DD/MM/YYYY",
            "DD-MM-YYYY",
            DATE_FORMATS.dbDate,
          ]}
          placeholder="dd/mm/yyyy"
          style={{ width: "100%" }}
          showToday={false}
          inputReadOnly={false}
        />
      </Form.Item>
    </>
  );
}
